import React from "react";
import {
  createElement,
  Fragment,
  useEffect,
  useRef,
  useState
} from "react";
import { render } from "react-dom";
import type { SearchClient } from "algoliasearch/lite";

import { usePagination, useSearchBox } from "react-instantsearch-hooks";
import { autocomplete, AutocompleteOptions, getAlgoliaResults, Render } from "@algolia/autocomplete-js";
import { BaseItem } from "@algolia/autocomplete-core";
import { useMemo } from "react";
import { createLocalStorageRecentSearchesPlugin, search } from "@algolia/autocomplete-plugin-recent-searches";
import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";

import {
  ABB_ORDERS_QUERY_SUGGESTIONS, ABB_ORDERS_INDEX_NAME, ABB_QUOTES_INDEX_NAME
} from "../constants";
import "@algolia/autocomplete-theme-classic";

type AutocompleteProps = Partial<AutocompleteOptions<BaseItem>> & {
  searchClient: SearchClient;
  className?: string;
};

type SetInstantSearchUiStateOptions = {
  query: string;
};

export function Autocomplete({
  searchClient,
  className,
  ...autocompleteProps
}: AutocompleteProps) {
  const autocompleteContainer = useRef<HTMLDivElement>(null);

  const { query, refine: setQuery } = useSearchBox();
  const { refine: setPage } = usePagination();

  const [instantSearchUiState, setInstantSearchUiState] = useState<
    SetInstantSearchUiStateOptions
  >({ query });

  useEffect(() => {
    setQuery(instantSearchUiState.query);
    setPage(0);
  }, [instantSearchUiState]);

  const plugins = useMemo(() => {
    const recentSearches = createLocalStorageRecentSearchesPlugin({
      key: "instantsearch",
      search(params) {
        return search({ ...params, limit: params.query ? 1 : 4 });
      },
    });

    const querySuggestions = createQuerySuggestionsPlugin({
      searchClient,
      indexName: ABB_ORDERS_QUERY_SUGGESTIONS,
      getSearchParams({state}) {
        return {
          ...recentSearches.data!.getAlgoliaSearchParams(),
          hitsPerPage: !state.query ? 0 : 4,
        }
      }
    });
    return [recentSearches, querySuggestions];
  }, []);

  useEffect(() => {
    if (!autocompleteContainer.current) {
      return;
    }

    const autocompleteInstance = autocomplete({
      ...autocompleteProps,
      container: autocompleteContainer.current,
      initialState: { query },
      onReset() {
        setInstantSearchUiState({ query: "" });
      },
      onSubmit({ state }) {
        setInstantSearchUiState({ query: state.query });
      },
      onStateChange({ prevState, state }) {
        if (prevState.query !== state.query) {
          setInstantSearchUiState({
            query: state.query
          });
        }
      },
      getSources({query}) {
          if(!query)
            return []

          return [
          {
            sourceId: 'orders',
            getItemInputValue({ item }) {
              return `${item.JobOrder}`;
            },
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ABB_ORDERS_INDEX_NAME,
                    query,
                    params: {
                      hitsPerPage: 4,
                    },
                  },
                ],
              });
            },
            templates: {
              item({ item, components, html }) {
                return html`<p>Job Order - ${components.ReverseHighlight({ hit: item, attribute: 'JobOrder' })}</p><p>Customer Name - ${components.ReverseHighlight({ hit: item, attribute: 'ReportingCustName' })}</p><p>SKU - ${components.ReverseHighlight({ hit: item, attribute: 'SKU' })}</p>`;
              },
            },
          },
          {
            sourceId: 'quotes',
            getItemInputValue({ item }) {
              return `${item.QuoteNo}`;
            },
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ABB_QUOTES_INDEX_NAME,
                    query,
                    params: {
                      hitsPerPage: 4,
                    },
                  },
                ],
              });
            },
            templates: {
              item({ item, components, html }) {
                return html`<p>Quote No. - ${components.ReverseHighlight({ hit: item, attribute: 'QuoteNo' })}</p><p>Customer Name - ${components.ReverseHighlight({ hit: item, attribute: 'CustomerName' })}</p>`;
              },
            },
          },
        ];
      },
      render({ elements }, root) {
      const { recentSearchesPlugin, querySuggestionsPlugin, orders, quotes } = elements;

        render(
          <div className="aa-PanelLayout aa-Panel--scrollable">
            <div className="aa-PanelSections">
              <div className="aa-PanelSection--left">
                <div>
                  <p><strong>Recent Searches</strong></p>
                  {recentSearchesPlugin}
                </div>
                <div>
                  <p><strong>Suggestions</strong></p>
                  {querySuggestionsPlugin}
                </div>
              </div>
              <div className="aa-PanelSection--right">
                <div className="orders-column">
                  <p><strong>Orders</strong></p>
                  {orders}
                </div>
                <div className="quotes-column">
                  <p><strong>Quotes</strong></p>
                  {quotes}
                </div>
              </div>
            </div>
          </div>,
          root
        );
      },
    renderer: { createElement, Fragment, render: () => {} },
    plugins,
    });

    return () => autocompleteInstance.destroy();
  }, [plugins]);

  return <div className={className} ref={autocompleteContainer} />;
}
